import React from 'react';
import { BlockType } from '../types/api/ElementType';
import { AnnouncementsContextBlock } from './Announcements';
import { StatusInfoWarning } from '../_lib/components/StatusInfo';
import { AppLibraryBlock } from './AppLibrary';
import { AppLibraryPersonalBlock } from './AppLibraryPersonal';
import { AttachedFilesBlock } from './AttachedFiles/AttachedFilesBlock';
import { BlogAuthorsBlock } from './BlogAuthors';
import { BlogPostSiblingsBlock } from './BlogPostSiblings';
import { CompositionConfigurationBlock } from './CompositionConfiguration/CompositionConfigurationBlock';
import { ConfigurationAggregateBlock } from './ConfigurationAggregate/ConfigurationAggregateBlock';
import { DeviatingValidityPeriodsBlock } from './DeviatingValidityPeriods';
import { ExternalLinksBlock, UserExternalLinksBlock } from './ExternalLinks/ExternalLinksBlock';
import { GenericSearchListBlock } from './GenericSearchList';
import { LatestAdditionsBlock } from './LatestAdditions';
import { NavigationPropertiesBlock } from './NavigationProperties/NavigationProperties';
import { PagePropertySettingsBlock } from './PagePropertySettings/PagePropertySettingsBlock';
import { PageToPageBlock, StructuralPageToPageBlock } from './PageToPage';
import { AnnouncementCategoriesBlock, FileCategoriesBlock, PageTypeCategoriesBlock } from './Categories';
import { PageTypeValidityPeriodBlock } from './PageTypeValidityPeriod';
import { PluginConfigurationBlock } from './PluginConfiguration/PluginConfigurationBlock';
import { TeamRoomMemberRequestsListBlock } from './TeamRoomMemberRequestsList';
import { TeaserInformationBlock } from './TeaserInformation/TeaserInformationBlock';
import { UpcomingEventsBlock } from './UpcomingEvents/UpcomingEventsBlock';
import { ContactPersonsBlock } from './ContactPersons';
import { TransformPageTypeBlock } from './TransformPageType/TransformPageTypeBlock';
import { OverallAreaGroupsBlock } from './OverallAreaGroups';
import { RoleSpecificAreaGroupsBlock } from './RoleSpecificAreaGroups/RoleSpecificAreaGroupsBlock';
import { AttributeGroupInfoBlock } from './AttributeGroupInfo';
import { RoleSettingsBlock } from './RoleSettings/RoleSettingsBlock';
import { RoleDefaultForPageTypeSettingsBlock } from './RoleSettings/RoleDefaultForPageTypeSettingsBlock';
import { RssFeedBlock } from './RssFeed/RssFeedBlock';
import { PageLayoutBlock } from './PageLayout/PageLayoutBlock';
import { AttributeGroupActivityBlock } from './AttributeGroupActivity';
import { LatestForumPostsBlock } from './LatestForumPosts/LatestForumPostsBlock';
import { PrivilegedGroupsBlock } from './PrivilegedGroups/PrivilegedGroupsBlock';
import { PageMembershipMatchingAttributesBlock }
  from './PageMembershipMatchingAttributes/PageMembershipMatchingAttributesBlock';
import { CalendarBlock } from './Calendar/CalendarBlock';
import { RolePrivilegeConfigBlock } from './RolePrivilegeConfig/RolePrivilegeConfigBlock';
import { PageTemplateSettingsBlock } from './PageTemplateSettings/PageTemplateSettings';
import { PendingAccessLevelRequestsBlock } from './fib/PendingAccessLevelRequestsBlock';
import { PageReadRestrictionsBlock } from './PageReadRestrictions/PageReadRestrictionsBlock';
import { EditorFeedbackBlock } from './Comments/CommentsBlock';
import { TeamRoomMemberRequestsBlock } from './TeamRoomMemberRequests/TeamRoomMemberRequestsBlock';
import { RoleValidForPageTypeSettingsBlock } from './RoleSettings/RoleValidForPageTypeSettingsBlock';
import {
  PageMembershipMatchingRuleSettingsBlock,
} from './PageMembershipMatchingRuleSettings/PageMembershipMatchingRuleSettingsBlock';
import { UserImportConfigurationBlock } from './UserImportConfiguration/UserImportConfigurationBlock';
import { UserSourcesListBlock } from './UserSourcesList/UserSourcesListBlock';

export type ContextBlock =
  | AnnouncementCategoriesBlock
  | AppLibraryBlock
  | AppLibraryPersonalBlock
  | AttachedFilesBlock
  | BlogAuthorsBlock
  | BlogPostSiblingsBlock
  | CompositionConfigurationBlock
  | ConfigurationAggregateBlock
  | ContactPersonsBlock
  | AnnouncementsContextBlock
  | DeviatingValidityPeriodsBlock
  | ExternalLinksBlock
  | UserExternalLinksBlock
  | GenericSearchListBlock
  | LatestAdditionsBlock
  | NavigationPropertiesBlock
  | PageLayoutBlock
  | PagePropertySettingsBlock
  | PageReadRestrictionsBlock
  | PageToPageBlock
  | PageTypeCategoriesBlock
  | PageTypeValidityPeriodBlock
  | PluginConfigurationBlock
  | RolePrivilegeConfigBlock
  | StructuralPageToPageBlock
  | TeamRoomMemberRequestsListBlock
  | OverallAreaGroupsBlock
  | RoleSpecificAreaGroupsBlock
  | TeaserInformationBlock
  | TransformPageTypeBlock
  | UpcomingEventsBlock
  | AttributeGroupInfoBlock
  | RoleSettingsBlock
  | RoleValidForPageTypeSettingsBlock
  | RoleDefaultForPageTypeSettingsBlock
  | RssFeedBlock
  | AttributeGroupActivityBlock
  | FileCategoriesBlock
  | LatestForumPostsBlock
  | PrivilegedGroupsBlock
  | PageMembershipMatchingAttributesBlock
  | PageMembershipMatchingRuleSettingsBlock
  | CalendarBlock
  | PageTemplateSettingsBlock
  | PendingAccessLevelRequestsBlock
  | EditorFeedbackBlock
  | TeamRoomMemberRequestsBlock
  | UserImportConfigurationBlock
  | UserSourcesListBlock;

export function mapContextBlock(contextBlock: ContextBlock) {
  switch (contextBlock.elementType) {
    case BlockType.applibrary:
      return <AppLibraryBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.applibraryPersonal:
      return <AppLibraryPersonalBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.attachedfiles:
      return <AttachedFilesBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.blogauthors:
      return <BlogAuthorsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.blogpostsiblings:
      return <BlogPostSiblingsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.compositionconfiguration:
      return <CompositionConfigurationBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.configurationaggregate:
      return <ConfigurationAggregateBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.contactpersons:
      return <ContactPersonsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.currentmandatoryannouncementsforpage:
    case BlockType.currentannouncementsoutbox:
    case BlockType.currentannouncementsinbox:
      return <AnnouncementsContextBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.deviatingvalidityperiods:
      return <DeviatingValidityPeriodsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.userexternallinks:
      return <UserExternalLinksBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.externallinks:
      return <ExternalLinksBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.genericsearchlist:
      return <GenericSearchListBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.latestadditions:
      return <LatestAdditionsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.navigationproperties:
      return <NavigationPropertiesBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pagelayout:
      return <PageLayoutBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pagepropertysettings:
      return <PagePropertySettingsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pagereadrestrictions:
      return <PageReadRestrictionsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pageToPage:
      return <PageToPageBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.announcementcategories:
      return <AnnouncementCategoriesBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pagetypecategories:
      return <PageTypeCategoriesBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pagetypevalidityperiod:
      return <PageTypeValidityPeriodBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pluginconfiguration:
      return <PluginConfigurationBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.roleprivilegeconfig:
      return <RolePrivilegeConfigBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.structuralPageToPage:
      return <StructuralPageToPageBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.teamroommemberrequestslist:
      return <TeamRoomMemberRequestsListBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.overallareagroups:
      return <OverallAreaGroupsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.rolespecificareagroups:
      return <RoleSpecificAreaGroupsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.teaserinformation:
      return <TeaserInformationBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.transformpagetype:
      return <TransformPageTypeBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.upcomingevents:
      return <UpcomingEventsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.attributegroupinfo:
      return <AttributeGroupInfoBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.rolesettings:
      return <RoleSettingsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.rolevalidforpagetypesettings:
      return <RoleValidForPageTypeSettingsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.roledefaultforpagetypesettings:
      return <RoleDefaultForPageTypeSettingsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.rssfeed:
      return <RssFeedBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.attributegroupactivity:
      return <AttributeGroupActivityBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.filecategories:
      return <FileCategoriesBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.latestforumposts:
      return <LatestForumPostsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.privilegedgroups:
      return <PrivilegedGroupsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pagemembershipmatchingattributes:
      return <PageMembershipMatchingAttributesBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pagemembershipmatchingrulesettings:
      return <PageMembershipMatchingRuleSettingsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.calendar:
      return <CalendarBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pagetemplate:
      return <PageTemplateSettingsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.pendingaccesslevelrequests:
      return <PendingAccessLevelRequestsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.editorFeedback:
      return <EditorFeedbackBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.teamroommemberrequests:
      return <TeamRoomMemberRequestsBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.userimportconfiguration:
      return <UserImportConfigurationBlock key={contextBlock.uuid} {...contextBlock} />;
    case BlockType.usersourceslist:
      return <UserSourcesListBlock key={contextBlock.uuid} {...contextBlock} />;
    default:
      // @ts-expect-error contextBlock might be any new, unknown block
      return <StatusInfoWarning key={contextBlock.elementType} modifier="padding" />;
  }
}
